// Pages for ad campaigns (/comecar and /comecar/:slug): one per niche, plus the generic one.
// Slugs are the ones of keepo.bio/para/<slug>; `category` is the Keepo's field ("ramo", see model/categories).
// Texts: pages.adSignup.<key>.{title,subtitle,example}; `example` is the invented Keepo drawn on the phone.
// `colors`: the page's ground, text and the card's button (ground and text: the same as the site's page of the niche,
// keepo.bio/para/<slug>, the Operations' palette `soft_*`); `accent` colors the example Keepo's
// buttons when the card's button would vanish on the page's ground (default: the button's colors).
export type AdNicheColors = {
  background: string;
  text: string;
  button: string;
  buttonText: string;
  accent?: string;
};
export type AdNiche = {
  slug: string | null;
  key: string;
  category: string | null;
  colors: AdNicheColors;
};

export const AD_NICHES: AdNiche[] = [
  {
    slug: null,
    key: "generic",
    category: null,
    colors: { background: "#fdf0f1", text: "#2a1418", button: "#ee0000", buttonText: "#ffffff" },
  },
  {
    slug: "psicologos",
    key: "psicologos",
    category: "health/psychology",
    colors: { background: "#f1e8e1", text: "#3b2a23", button: "#5a3e33", buttonText: "#ffffff" },
  },
  {
    slug: "nutricionistas",
    key: "nutricionistas",
    category: "health/nutrition",
    colors: { background: "#eef1ea", text: "#14170f", button: "#14170f", buttonText: "#ffffff" },
  },
  {
    slug: "dentistas",
    key: "dentistas",
    category: "health/dentistry",
    colors: { background: "#fff3ea", text: "#6b3416", button: "#f4a47c", buttonText: "#3a1a08" },
  },
  {
    slug: "fisioterapeutas",
    key: "fisioterapeutas",
    category: "health/physiotherapy",
    colors: { background: "#e6f3ef", text: "#0f3b33", button: "#0f3b33", buttonText: "#ffffff" },
  },
  {
    slug: "personal-trainers",
    key: "personalTrainers",
    category: "fitness/personal-trainer",
    colors: { background: "#eaf2e3", text: "#18230f", button: "#2f6b2f", buttonText: "#ffffff" },
  },
  {
    slug: "esteticistas",
    key: "esteticistas",
    category: "beauty/aesthetics",
    colors: { background: "#f4f0ec", text: "#151515", button: "#151515", buttonText: "#ffffff" },
  },
  {
    slug: "advogados",
    key: "advogados",
    category: "services/law",
    colors: { background: "#eef0f4", text: "#1b2540", button: "#1b2540", buttonText: "#ffffff" },
  },
  {
    slug: "medicos",
    key: "medicos",
    category: "health/doctor",
    colors: { background: "#eaf2f6", text: "#10212b", button: "#10212b", buttonText: "#ffffff" },
  },
  {
    slug: "terapeutas",
    key: "terapeutas",
    category: "fitness/holistic",
    colors: { background: "#f1ecf7", text: "#2e2340", button: "#5b4b8a", buttonText: "#ffffff" },
  },
  {
    slug: "profissionais-de-saude",
    key: "profissionaisDeSaude",
    category: "health",
    colors: { background: "#e6f2ea", text: "#14532d", button: "#14532d", buttonText: "#ffffff" },
  },
  {
    slug: "coaches-e-mentores",
    key: "coachesEMentores",
    category: "education/mentoring",
    colors: { background: "#e3cfae", text: "#4a2a12", button: "#6b3a14", buttonText: "#ffffff" },
  },
  {
    slug: "corretores-de-imoveis",
    key: "corretoresDeImoveis",
    category: "services/real-estate",
    colors: { background: "#f1eee6", text: "#1f2a1f", button: "#2f5d3a", buttonText: "#ffffff" },
  },
  {
    slug: "arquitetos",
    key: "arquitetos",
    category: "services/architecture",
    colors: { background: "#e9e8e4", text: "#1a1a1a", button: "#1a1a1a", buttonText: "#ffffff" },
  },
  {
    slug: "fotografos",
    key: "fotografos",
    category: "creators/photography",
    colors: { background: "#ececea", text: "#141414", button: "#141414", buttonText: "#ffffff" },
  },
  {
    slug: "negocios-de-alimentacao",
    key: "negociosDeAlimentacao",
    category: "food",
    colors: { background: "#fff6e6", text: "#3d2a10", button: "#f5a524", buttonText: "#2a1a05" },
  },
  {
    slug: "lojas",
    key: "lojas",
    category: "retail/store",
    colors: { background: "#ece6d6", text: "#3d2a1a", button: "#f08a12", buttonText: "#2a1405" },
  },
  {
    slug: "contadores",
    key: "contadores",
    category: "services/accounting",
    colors: { background: "#eaf1fb", text: "#102a54", button: "#1f4fa3", buttonText: "#ffffff" },
  },
  {
    slug: "tatuadores",
    key: "tatuadores",
    category: "beauty/tattoo",
    colors: { background: "#e9e9ec", text: "#16181d", button: "#16181d", buttonText: "#ffffff" },
  },
  {
    slug: "pet-shops",
    key: "petShops",
    category: "pets/pet-shop",
    colors: { background: "#fff1d6", text: "#3a2208", button: "#f28c28", buttonText: "#2a1405" },
  },
  {
    slug: "igrejas",
    key: "igrejas",
    category: "community/religious",
    colors: { background: "#f4efe6", text: "#2b2340", button: "#4b3f72", buttonText: "#ffffff" },
  },
  {
    slug: "artistas",
    key: "artistas",
    category: "creators",
    colors: { background: "#fbf0e1", text: "#2b1a08", button: "#b95d0e", buttonText: "#ffffff" },
  },
  {
    slug: "freelancers",
    key: "freelancers",
    category: "services",
    colors: { background: "#fbf1ea", text: "#1d3f8a", button: "#3b74d6", buttonText: "#ffffff" },
  },
];

const GENERIC_NICHE = AD_NICHES[0];

/** Unknown or missing slug shows the generic page. */
export const adNicheBySlug = (slug?: string | null): AdNiche =>
  (slug && AD_NICHES.find(niche => niche.slug === slug)) || GENERIC_NICHE;
